import { useState, useEffect } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

interface PgDatabase { id: string; name: string; schema_name: string; status: string; created_at: string; project_id?: string }
interface PgTable { name: string; rowCount: number }
interface Project { id: string; name: string; slug?: string; framework?: string }
interface Column { name: string; type: string; nullable?: boolean; default?: string }

type View = 'projects' | 'databases' | 'tables' | 'table-data' | 'create-table' | 'add-row' | 'sql'

const COLUMN_TYPES = ['TEXT', 'INTEGER', 'BOOLEAN', 'TIMESTAMP', 'SERIAL', 'UUID', 'DECIMAL', 'JSON']

const TABLE_TEMPLATES = [
  { name: 'users', desc: 'User accounts', columns: [
    { name: 'id', type: 'SERIAL', nullable: false },
    { name: 'email', type: 'TEXT', nullable: false },
    { name: 'username', type: 'TEXT', nullable: false },
    { name: 'password_hash', type: 'TEXT', nullable: false },
    { name: 'created_at', type: 'TIMESTAMP', default: 'NOW()' },
  ]},
  { name: 'products', desc: 'Store products', columns: [
    { name: 'id', type: 'SERIAL', nullable: false },
    { name: 'name', type: 'TEXT', nullable: false },
    { name: 'description', type: 'TEXT' },
    { name: 'price', type: 'DECIMAL', nullable: false },
    { name: 'stock', type: 'INTEGER', default: '0' },
    { name: 'created_at', type: 'TIMESTAMP', default: 'NOW()' },
  ]},
  { name: 'posts', desc: 'Blog posts', columns: [
    { name: 'id', type: 'SERIAL', nullable: false },
    { name: 'title', type: 'TEXT', nullable: false },
    { name: 'content', type: 'TEXT' },
    { name: 'author_id', type: 'INTEGER' },
    { name: 'published', type: 'BOOLEAN', default: 'false' },
    { name: 'created_at', type: 'TIMESTAMP', default: 'NOW()' },
  ]},
  { name: 'orders', desc: 'Customer orders', columns: [
    { name: 'id', type: 'SERIAL', nullable: false },
    { name: 'user_id', type: 'INTEGER', nullable: false },
    { name: 'total', type: 'DECIMAL', nullable: false },
    { name: 'status', type: 'TEXT', default: "'pending'" },
    { name: 'created_at', type: 'TIMESTAMP', default: 'NOW()' },
  ]},
  { name: 'tasks', desc: 'Task management', columns: [
    { name: 'id', type: 'SERIAL', nullable: false },
    { name: 'title', type: 'TEXT', nullable: false },
    { name: 'completed', type: 'BOOLEAN', default: 'false' },
    { name: 'priority', type: 'TEXT', default: "'medium'" },
    { name: 'user_id', type: 'INTEGER' },
    { name: 'due_date', type: 'TIMESTAMP' },
    { name: 'created_at', type: 'TIMESTAMP', default: 'NOW()' },
  ]},
]

export function DatabasePanel({ projectId, projectSlug }: { projectId?: string; projectSlug?: string } = {}) {
  const [view, setView] = useState<View>(projectId ? 'databases' : 'projects')
  const [projects, setProjects] = useState<Project[]>([])
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [databases, setDatabases] = useState<PgDatabase[]>([])
  const [selectedDb, setSelectedDb] = useState<PgDatabase | null>(null)
  const [tables, setTables] = useState<PgTable[]>([])
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState('')
  const [creating, setCreating] = useState(false)

  // Table creation state
  const [newTableName, setNewTableName] = useState('')
  const [newColumns, setNewColumns] = useState<Column[]>([{ name: 'id', type: 'SERIAL', nullable: false }])

  // Row state
  const [tableData, setTableData] = useState<{ columns: string[]; rows: any[] }>({ columns: [], rows: [] })
  const [activeTable, setActiveTable] = useState('')
  const [newRow, setNewRow] = useState<Record<string, string>>({})

  // SQL state
  const [sqlQuery, setSqlQuery] = useState('')
  const [sqlResults, setSqlResults] = useState<{ columns: string[]; rows: any[] } | null>(null)

  useEffect(() => {
    authFetch(apiUrl('/api/projects')).then(r => r.json()).then(d => {
      const list = Array.isArray(d) ? d : d.projects || []
      setProjects(list)
      // If projectId passed, auto-select that project
      if (projectId) {
        const match = list.find((p: Project) => p.id === projectId || p.id.startsWith(projectId.split('-')[0]) || p.slug === projectSlug || p.name === projectSlug)
        if (match) selectProject(match)
      }
    }).catch(() => {})
  }, [projectId, projectSlug])

  const showResult = (msg: string) => { setResult(msg); setTimeout(() => setResult(''), 4000) }

  const selectProject = async (p: Project) => {
    setSelectedProject(p)
    setLoading(true)
    try {
      const res = await authFetch(apiUrl('/api/postgres'))
      const data = await res.json()
      const dbs = Array.isArray(data?.databases) ? data.databases : Array.isArray(data) ? data : []
      const filtered = dbs.filter((db: PgDatabase) => db.project_id === p.id || db.name === p.slug || db.name === p.name)
      setDatabases(filtered)
      setView(filtered.length > 0 ? 'databases' : 'databases')
    } catch {}
    setLoading(false)
  }

  const createDatabase = async () => {
    if (!selectedProject) return
    setCreating(true)
    try {
      const res = await authFetch(apiUrl('/api/postgres'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: selectedProject.slug || selectedProject.name, projectId: selectedProject.id }),
      })
      const data = await res.json()
      if (data.id) {
        showResult('✓ Database created!')
        await selectProject(selectedProject)
      } else { showResult(`✗ ${data.error}`) }
    } catch (e: any) { showResult(`✗ ${e.message}`) }
    setCreating(false)
  }

  const openDb = async (db: PgDatabase) => {
    setSelectedDb(db)
    setLoading(true)
    try {
      const res = await authFetch(apiUrl(`/api/postgres/${db.id}/tables?_t=${Date.now()}`))
      const data = await res.json()
      setTables(Array.isArray(data) ? data : [])
    } catch { setTables([]) }
    setLoading(false)
    setView('tables')
  }

  const refreshTables = async () => {
    if (!selectedDb) return
    const res = await authFetch(apiUrl(`/api/postgres/${selectedDb.id}/tables?_t=${Date.now()}`))
    const data = await res.json()
    setTables(Array.isArray(data) ? data : [])
  }

  const createTable = async () => {
    if (!selectedDb || !newTableName.trim() || newColumns.length === 0) return
    const cols = newColumns.map(c => {
      let def = `"${c.name}" ${c.type}`
      if (c.type === 'SERIAL') def = `"${c.name}" SERIAL PRIMARY KEY`
      else {
        if (!c.nullable) def += ' NOT NULL'
        if (c.default) def += ` DEFAULT ${c.default}`
      }
      return def
    }).join(', ')
    const sql = `CREATE TABLE "${newTableName}" (${cols})`
    try {
      const res = await authFetch(apiUrl(`/api/postgres/${selectedDb.id}/query`), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sql }),
      })
      const data = await res.json()
      if (data.error) { showResult(`✗ ${data.error}`) }
      else { showResult(`✓ Table "${newTableName}" created!`); setNewTableName(''); setNewColumns([{ name: 'id', type: 'SERIAL', nullable: false }]); await refreshTables(); setView('tables') }
    } catch (e: any) { showResult(`✗ ${e.message}`) }
  }

  const useTemplate = (tpl: typeof TABLE_TEMPLATES[0]) => {
    setNewTableName(tpl.name)
    setNewColumns(tpl.columns.map(c => ({ name: c.name, type: c.type, nullable: c.nullable !== false, default: c.default })))
  }

  const openTable = async (tableName: string) => {
    if (!selectedDb) return
    setActiveTable(tableName)
    try {
      const res = await authFetch(apiUrl(`/api/postgres/${selectedDb.id}/query`), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sql: `SELECT * FROM "${tableName}" ORDER BY 1 DESC LIMIT 100` }),
      })
      const data = await res.json()
      if (data.error) { showResult(`✗ ${data.error}`); return }
      setTableData({ columns: data.columns ?? [], rows: data.rows ?? [] })
      // Open table data in the main editor area
      window.dispatchEvent(new CustomEvent('pulse:open-table', {
        detail: { dbId: selectedDb.id, dbName: selectedDb.name, tableName, columns: data.columns ?? [], rows: data.rows ?? [] }
      }))
      setView('table-data')
    } catch (e: any) { showResult(`✗ ${e.message}`) }
  }

  const deleteRow = async (row: any) => {
    if (!selectedDb || !activeTable) return
    const firstCol = tableData.columns[0]
    if (!firstCol) return
    const val = row[firstCol]
    if (!window.confirm(`Delete row where ${firstCol} = ${val}?`)) return
    try {
      await authFetch(apiUrl(`/api/postgres/${selectedDb.id}/query`), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sql: `DELETE FROM "${activeTable}" WHERE "${firstCol}" = '${val}'` }),
      })
      showResult('✓ Row deleted')
      openTable(activeTable)
    } catch (e: any) { showResult(`✗ ${e.message}`) }
  }

  const insertRow = async () => {
    if (!selectedDb || !activeTable) return
    const cols = Object.keys(newRow).filter(k => newRow[k]?.trim())
    if (cols.length === 0) return
    const values = cols.map(k => `'${newRow[k].replace(/'/g, "''")}'`)
    const sql = `INSERT INTO "${activeTable}" (${cols.map(c => `"${c}"`).join(', ')}) VALUES (${values.join(', ')})`
    try {
      const res = await authFetch(apiUrl(`/api/postgres/${selectedDb.id}/query`), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sql }),
      })
      const data = await res.json()
      if (data.error) { showResult(`✗ ${data.error}`) }
      else { showResult('✓ Row added!'); setNewRow({}); openTable(activeTable) }
    } catch (e: any) { showResult(`✗ ${e.message}`) }
  }

  const dropTable = async (name: string) => {
    if (!selectedDb) return
    if (!window.confirm(`Drop table "${name}"? This deletes all data permanently.`)) return
    try {
      await authFetch(apiUrl(`/api/postgres/${selectedDb.id}/query`), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sql: `DROP TABLE "${name}"` }),
      })
      showResult(`✓ Table "${name}" dropped`)
      refreshTables()
    } catch (e: any) { showResult(`✗ ${e.message}`) }
  }

  const runSql = async () => {
    if (!selectedDb || !sqlQuery.trim()) return
    try {
      const res = await authFetch(apiUrl(`/api/postgres/${selectedDb.id}/query`), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sql: sqlQuery }),
      })
      const data = await res.json()
      if (data.error) { showResult(`✗ ${data.error}`); setSqlResults(null) }
      else if (data.rows?.length > 0) { setSqlResults({ columns: data.columns, rows: data.rows }); showResult(`✓ ${data.rows.length} rows`)
        window.dispatchEvent(new CustomEvent('pulse:open-table', {
          detail: { dbId: selectedDb.id, dbName: selectedDb.name, tableName: 'Query Result', columns: data.columns, rows: data.rows }
        }))
      }
      else { showResult(`✓ Done (${data.rowCount ?? 0} rows affected)`); setSqlResults(null); refreshTables() }
    } catch (e: any) { showResult(`✗ ${e.message}`) }
  }

  // ─── RENDER ───
  const renderHeader = (title: string, onBack?: () => void) => (
    <div style={headerStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {onBack && <button onClick={onBack} style={iconBtn}>←</button>}
        <span>{title}</span>
      </div>
      {result && <span style={{ fontSize: 9, color: result.startsWith('✓') ? '#5fd4a4' : '#f07070', fontFamily: 'var(--font-mono)' }}>{result}</span>}
    </div>
  )

  // Project selection
  if (view === 'projects') {
    return (
      <div style={panelStyle}>
        {renderHeader('DATABASE')}
        <div style={{ padding: '10px 14px', fontSize: 10, color: 'var(--text-2)', lineHeight: 1.6 }}>
          Select a project to manage its database. Each project gets an isolated PostgreSQL schema.
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {projects.map(p => (
            <button key={p.id} onClick={() => selectProject(p)} style={listItemBtn}>
              <span style={{ fontSize: 14 }}>{p.framework === 'react' ? '⚛️' : p.framework === 'node' ? '📦' : '📄'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-0)' }}>{p.name}</div>
                <div style={{ fontSize: 9, color: 'var(--text-2)' }}>{p.framework || 'project'}</div>
              </div>
              <span style={{ fontSize: 10, color: 'var(--text-2)' }}>→</span>
            </button>
          ))}
        </div>
      </div>
    )
  }

  // Database list for project
  if (view === 'databases') {
    return (
      <div style={panelStyle}>
        {renderHeader(`${selectedProject?.name} / DB`, () => { setView('projects'); setSelectedProject(null) })}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? <div style={emptyStyle}>Loading...</div> : databases.length === 0 ? (
            <div style={emptyStyle}>
              <span style={{ fontSize: 32 }}>🐘</span>
              <span style={{ fontWeight: 700, color: 'var(--text-0)' }}>No database yet</span>
              <span style={{ fontSize: 10, color: 'var(--text-2)', textAlign: 'center', lineHeight: 1.6 }}>
                Create a PostgreSQL database to store users, products, orders, and more.
              </span>
              <button onClick={createDatabase} disabled={creating} style={{ ...btnStyle, marginTop: 8 }}>
                {creating ? '...' : '+ Create Database'}
              </button>
            </div>
          ) : (
            <>
              {databases.map(db => (
                <button key={db.id} onClick={() => openDb(db)} style={listItemBtn}>
                  <span>🐘</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-0)' }}>{db.name}</div>
                    <div style={{ fontSize: 9, color: 'var(--text-2)' }}>{db.schema_name}</div>
                  </div>
                  <span style={{ fontSize: 10, color: 'var(--text-2)' }}>→</span>
                </button>
              ))}
            </>
          )}
        </div>
      </div>
    )
  }

  // Tables list
  if (view === 'tables') {
    return (
      <div style={panelStyle}>
        {renderHeader(`${selectedDb?.name} / Tables`, () => { setView('databases') })}
        <div style={{ display: 'flex', gap: 4, padding: '6px 12px', borderBottom: '1px solid var(--border)' }}>
          <button onClick={() => setView('create-table')} style={btnStyle}>+ New Table</button>
          <button onClick={() => { setSqlQuery(''); setSqlResults(null); setView('sql') }} style={btnStyle}>SQL</button>
          <button onClick={refreshTables} style={iconBtn}>↻</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {tables.length === 0 ? (
            <div style={emptyStyle}>
              <span style={{ fontSize: 24 }}>📋</span>
              <span style={{ fontWeight: 700, color: 'var(--text-0)' }}>No tables yet</span>
              <span style={{ fontSize: 10, color: 'var(--text-2)', textAlign: 'center', lineHeight: 1.6 }}>
                Create your first table or use a template below.
              </span>
            </div>
          ) : tables.map(t => (
            <div key={t.name} style={{ display: 'flex', alignItems: 'center', padding: '8px 14px', borderBottom: '1px solid var(--border)', gap: 8 }}>
              <button onClick={() => openTable(t.name)} style={{ ...listItemBtn, border: 'none', padding: 0, flex: 1 }}>
                <span>📋</span>
                <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: 'var(--text-0)' }}>{t.name}</span>
              </button>
              <button onClick={() => dropTable(t.name)} style={{ ...delBtn, fontSize: 9, color: 'var(--red)' }} title="Drop table">✕</button>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Create table view
  if (view === 'create-table') {
    return (
      <div style={panelStyle}>
        {renderHeader('Create Table', () => setView('tables'))}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>
          {/* Templates */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-2)', marginBottom: 6, letterSpacing: '0.08em' }}>TEMPLATES</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {TABLE_TEMPLATES.map(t => (
                <button key={t.name} onClick={() => useTemplate(t)} style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-2)', color: 'var(--text-1)', fontSize: 10, cursor: 'pointer' }}>
                  {t.name}
                </button>
              ))}
            </div>
          </div>

          {/* Table name */}
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Table name</label>
            <input value={newTableName} onChange={e => setNewTableName(e.target.value)} placeholder="e.g. users, products, orders" style={inputStyle} />
          </div>

          {/* Columns */}
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Columns</label>
            {newColumns.map((col, i) => (
              <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                <input value={col.name} onChange={e => { const c = [...newColumns]; c[i].name = e.target.value; setNewColumns(c) }} placeholder="name" style={{ ...inputStyle, flex: 2 }} />
                <select value={col.type} onChange={e => { const c = [...newColumns]; c[i].type = e.target.value; setNewColumns(c) }} style={{ ...inputStyle, flex: 1 }}>
                  {COLUMN_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <button onClick={() => setNewColumns(newColumns.filter((_, j) => j !== i))} style={{ ...delBtn, color: 'var(--red)' }}>✕</button>
              </div>
            ))}
            <button onClick={() => setNewColumns([...newColumns, { name: '', type: 'TEXT' }])} style={{ ...btnStyle, width: '100%', marginTop: 4 }}>+ Add Column</button>
          </div>

          <button onClick={createTable} disabled={!newTableName.trim()} style={{ ...btnStyle, width: '100%', padding: '8px', background: 'rgba(95,212,164,0.1)', borderColor: '#5fd4a4', color: '#5fd4a4' }}>
            Create Table
          </button>
        </div>
      </div>
    )
  }

  // Table data view
  if (view === 'table-data') {
    return (
      <div style={panelStyle}>
        {renderHeader(`${activeTable}`, () => setView('tables'))}
        <div style={{ display: 'flex', gap: 4, padding: '6px 12px', borderBottom: '1px solid var(--border)' }}>
          <button onClick={() => { setNewRow({}); setView('add-row') }} style={btnStyle}>+ Add Row</button>
          <button onClick={() => openTable(activeTable)} style={iconBtn}>↻</button>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 9, color: 'var(--text-2)', alignSelf: 'center' }}>{tableData.rows.length} rows</span>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          {tableData.rows.length === 0 ? (
            <div style={emptyStyle}>
              <span>Empty table</span>
              <button onClick={() => { setNewRow({}); setView('add-row') }} style={btnStyle}>+ Add first row</button>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, fontFamily: 'var(--font-mono)' }}>
              <thead>
                <tr>
                  {tableData.columns.map(c => <th key={c} style={thStyle}>{c}</th>)}
                  <th style={thStyle}>⚙</th>
                </tr>
              </thead>
              <tbody>
                {tableData.rows.map((row, i) => (
                  <tr key={i}>
                    {tableData.columns.map(c => <td key={c} style={tdStyle}>{row[c] === null ? <em style={{ color: 'var(--text-2)' }}>null</em> : String(row[c])}</td>)}
                    <td style={tdStyle}><button onClick={() => deleteRow(row)} style={{ ...delBtn, color: 'var(--red)', fontSize: 9 }}>🗑</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    )
  }

  // Add row view
  if (view === 'add-row') {
    return (
      <div style={panelStyle}>
        {renderHeader(`Add Row → ${activeTable}`, () => setView('table-data'))}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>
          {tableData.columns.filter(c => c !== 'id').map(col => (
            <div key={col} style={{ marginBottom: 8 }}>
              <label style={labelStyle}>{col}</label>
              <input value={newRow[col] ?? ''} onChange={e => setNewRow({ ...newRow, [col]: e.target.value })} placeholder={`Enter ${col}...`} style={inputStyle} />
            </div>
          ))}
          <button onClick={insertRow} style={{ ...btnStyle, width: '100%', padding: '8px', background: 'rgba(95,212,164,0.1)', borderColor: '#5fd4a4', color: '#5fd4a4' }}>
            Insert Row
          </button>
        </div>
      </div>
    )
  }

  // SQL view
  if (view === 'sql') {
    return (
      <div style={panelStyle}>
        {renderHeader('SQL Query', () => setView('tables'))}
        <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
          <textarea value={sqlQuery} onChange={e => setSqlQuery(e.target.value)} placeholder="SELECT * FROM users WHERE..." rows={4} style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }} />
          <button onClick={runSql} disabled={!sqlQuery.trim()} style={{ ...btnStyle, width: '100%', marginTop: 6 }}>▶ Run SQL</button>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          {sqlResults ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, fontFamily: 'var(--font-mono)' }}>
              <thead><tr>{sqlResults.columns.map(c => <th key={c} style={thStyle}>{c}</th>)}</tr></thead>
              <tbody>{sqlResults.rows.map((row, i) => (
                <tr key={i}>{sqlResults.columns.map(c => <td key={c} style={tdStyle}>{row[c] === null ? 'null' : String(row[c])}</td>)}</tr>
              ))}</tbody>
            </table>
          ) : (
            <div style={{ padding: 14, fontSize: 10, color: 'var(--text-2)', lineHeight: 1.8, fontFamily: 'var(--font-mono)' }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Quick examples:</div>
              <div style={{ cursor: 'pointer', color: 'var(--accent)' }} onClick={() => setSqlQuery('SELECT * FROM "users" LIMIT 10')}>SELECT * FROM "users" LIMIT 10</div>
              <div style={{ cursor: 'pointer', color: 'var(--accent)' }} onClick={() => setSqlQuery('INSERT INTO "users" (email, username) VALUES (\'test@test.com\', \'testuser\')')}>INSERT INTO "users" (...)</div>
              <div style={{ cursor: 'pointer', color: 'var(--accent)' }} onClick={() => setSqlQuery('UPDATE "users" SET username = \'new\' WHERE id = 1')}>UPDATE "users" SET ...</div>
              <div style={{ cursor: 'pointer', color: 'var(--accent)' }} onClick={() => setSqlQuery('DELETE FROM "users" WHERE id = 1')}>DELETE FROM "users" WHERE ...</div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return null
}

const panelStyle: React.CSSProperties = { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-1)', borderRight: '1px solid var(--border)', overflow: 'hidden' }
const headerStyle: React.CSSProperties = { padding: '8px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-2)', textTransform: 'uppercase', borderBottom: '1px solid var(--border)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }
const emptyStyle: React.CSSProperties = { padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: 'var(--text-2)', fontSize: 11, fontFamily: 'var(--font-mono)' }
const inputStyle: React.CSSProperties = { padding: '6px 8px', borderRadius: 4, border: '1px solid var(--border-2)', background: 'var(--bg-2)', color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontSize: 11, outline: 'none', width: '100%' }
const btnStyle: React.CSSProperties = { padding: '5px 12px', borderRadius: 4, border: '1px solid var(--accent)', background: 'rgba(124,110,245,0.08)', color: 'var(--accent)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700, flexShrink: 0 }
const iconBtn: React.CSSProperties = { background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', display: 'flex', alignItems: 'center', fontSize: 14 }
const delBtn: React.CSSProperties = { background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', padding: 4, borderRadius: 4, display: 'flex', alignItems: 'center' }
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 9, fontWeight: 700, color: 'var(--text-2)', marginBottom: 3, letterSpacing: '0.05em' }
const listItemBtn: React.CSSProperties = { width: '100%', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', borderBottom: '1px solid var(--border)', cursor: 'pointer', textAlign: 'left' }
const thStyle: React.CSSProperties = { padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid var(--border)', color: 'var(--accent)', fontWeight: 700, position: 'sticky', top: 0, background: 'var(--bg-1)', fontSize: 9 }
const tdStyle: React.CSSProperties = { padding: '4px 8px', borderBottom: '1px solid var(--border)', color: 'var(--text-1)', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
